import React from 'react';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import copyMediaElementOperation from 'editor/src/store/design/operation/copyMediaElementOperation';
import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import addSelectedMediaElementsOperation from 'editor/src/store/editor/operation/addSelectedMediaElementsOperation';
import isAddElementsAllowed from 'editor/src/store/editor/selector/isAddElementsAllowed';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import getPreviousStatesCount from 'editor/src/store/editorModules/undoRedo/selector/getPreviousStatesCount';
import { popPreviousStateAction } from 'editor/src/store/editorModules/undoRedo/slice';
import { useDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';

import IconCopy from 'editor/src/component/Icon/IconCopy';
import { CanShow, MenuItemProps } from 'editor/src/component/Menu/type';

import MenuButton from './MenuButton';

export const canShow: CanShow = (state, { hasSelection, isElementEmpty, structureIndexes }) => {
  if (!hasSelection || isElementEmpty || !isAddElementsAllowed(state)) {
    return false;
  }
  const element = getMediaElement(state, structureIndexes[0]);
  return !!element && !element.unduplicable;
};

type Props = Pick<MenuItemProps, 'context' | 'mode' | 'close'>;

function ButtonDuplicate({ context, mode, close }: Props) {
  const dispatch = useDispatch();

  const handleClick = () => {
    batch(() => {
      const stepsBefore = dispatch((_, getState: () => RootState) => getPreviousStatesCount(getState()));
      dispatch(saveUndoRedoStateOperation('Duplicate element'));
      const copiedElementUids: number[] = [];
      // a copy can be refused (a full rolling gang sheet explains why itself); the rest of
      // the selection is not attempted, so the buyer sees that message once
      context.structureIndexes.some((structureIndex) => {
        const newElement = dispatch(copyMediaElementOperation(structureIndex, undefined, false));
        if (!newElement) {
          return true;
        }
        copiedElementUids.push(newElement.uuid);
        return false;
      });

      // nothing was copied: drop the undo step this click saved, or Undo would do nothing
      if (copiedElementUids.length === 0) {
        dispatch((innerDispatch, getState: () => RootState) => {
          if (getPreviousStatesCount(getState()) > stepsBefore) {
            innerDispatch(popPreviousStateAction());
          }
        });
        return;
      }

      dispatch(addSelectedMediaElementsOperation(copiedElementUids));
    });
    close?.();
  };

  return (
    <MenuButton
      Icon={IconCopy}
      mode={mode}
      handleClick={handleClick}
      i18nKey="editor-duplicate"
      className="cy-button-duplicate"
    />
  );
}

export default React.memo(ButtonDuplicate);
